import React from 'react'
import { Split, TriangleAlert, CalendarRange } from 'lucide-react'
import { moneyOrBlank, pctOrBlank, monthLabel, signTone } from './overview'
import DetailNote from './DetailNote'

// ─────────────────────────────────────────────────────────────────────────────
//   La cascada de reparto — tres estados, y cada uno se ve distinto
//
//     policy_resolved false  → las cifras vienen en null. "Sin política
//                              vigente", NUNCA ceros: un cero y un "no se pudo
//                              calcular" se ven igual y significan cosas
//                              opuestas. Ésta es la forma de los peores bugs
//                              del sistema que esto reemplaza, y es la razón de
//                              que el backend tenga dos flags en vez de una
//                              función que devuelva ceros.
//     negative_excess true   → ceros REALES: se calculó y la respuesta es
//                              ninguna. Hoy este es el caso normal, así que no
//                              puede parecer un fallo — tono neutro, sin rojo y
//                              sin iconos de alarma.
//     excedente positivo     → la cascada completa con el desglose por socio.
//
//   Los porcentajes salen SIEMPRE de `allocation.policy`. Escribirlos aquí
//   haría que la pantalla mintiera en cuanto el contable cambie la tasa: la
//   política está versionada por fecha y un mes ya reportado conserva la suya,
//   garantía que un literal en el cliente rompería desde fuera de la base.
//
//   `distribution.by_owner` se pinta en los TRES estados, que es como lo manda
//   el backend: una tabla que gana y pierde filas entre meses es una tabla que
//   nadie puede maquetar, y la ausencia de una fila se leería como que ese
//   socio no cobra.
//
// ─────────────────────────────────────────────────────────────────────────────
//   Y un CUARTO estado, que no es uno de los tres: el rango
//
//   Sobre una ventana de más de un mes el backend no manda esos tres estados
//   con las cifras vacías, manda OTRA FORMA — `{applies_to_range: false,
//   detail, months}` — con las cifras AUSENTES. Es deliberado y es la parte que
//   este componente no puede estropear: un null ahí se leería como "no había
//   política" y un 0 como "mes en déficit", que son justo los dos estados de
//   arriba, y el bloque existe para mantenerlos separados.
//
//   Por eso se ramifica por `applies_to_range` y NUNCA por si `tax_reserve`
//   está o no está. El esquema de ninja es una unión discriminada por esa
//   bandera, las dos formas la llevan, y leer la bandera primero es lo único
//   que impide confundir una forma con la otra. `'tax_reserve' in allocation`
//   daría hoy la misma respuesta y dejaría de darla en cuanto el backend añada
//   un campo, que es la clase de acierto que se convierte en fallo solo.
//
//   Con el rango largo se pinta el `detail` del backend —literal, arriba y sin
//   plegar— y debajo la cascada de UN mes del rango, traída aparte con
//   `?month=`. Se pinta la del último mes de la ventana en vez de dejar el
//   bloque vacío hasta que alguien elija, y la honestidad la cargan el `detail`
//   y el encabezado, que nombra el mes y dice de qué ventana es un mes.
// ─────────────────────────────────────────────────────────────────────────────

function WaterfallRow({ label, pct, value, hint, indent, strong, muted, tone }) {
  return (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        gap: 12, padding: '8px 0', paddingLeft: indent ? 18 : 0,
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: strong ? 700 : 500, color: 'var(--text-primary)' }}>
            {label}
          </span>
          {pct && <span className="badge badge-gray" style={{ fontSize: 10 }}>{pct}</span>}
        </div>
        {hint && (
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.45 }}>{hint}</div>
        )}
      </div>
      <span
        style={{
          fontSize: strong ? 15 : 13.5,
          fontWeight: strong ? 700 : 600,
          whiteSpace: 'nowrap',
          // El tono sólo se aplica cuando hay cifra: `muted` es el estado sin
          // política, donde lo que se pinta es un guion y darle color sería
          // colorear una respuesta que no existe.
          color: muted ? 'var(--text-muted)' : (tone || 'var(--text-primary)'),
          fontStyle: muted ? 'italic' : 'normal',
        }}
      >
        {value}
      </span>
    </div>
  )
}

function OwnerTable({ distribution, blank, resolved }) {
  const owners = distribution?.by_owner || []
  if (owners.length === 0) return null

  // `allocated` sólo difiere de `total` cuando las participaciones no suman 1,
  // lo cual es legítimo durante un cambio de accionariado. El backend lo
  // reporta en vez de forzarlo, así que la pantalla lo enseña en vez de
  // taparlo. Se compara sólo cuando ambos son números: en el estado sin
  // política los dos son null y no hay nada que comparar.
  const bothKnown =
    distribution?.total !== null && distribution?.total !== undefined &&
    distribution?.allocated !== null && distribution?.allocated !== undefined
  const shortfall = bothKnown && distribution.total !== distribution.allocated

  return (
    <div style={{ marginTop: 4 }}>
      {owners.map((owner) => (
        <WaterfallRow
          key={owner.owner_id}
          indent
          label={owner.name}
          pct={pctOrBlank(owner.ownership_pct)}
          value={owner.amount === null || owner.amount === undefined ? blank : moneyOrBlank(owner.amount)}
          muted={!resolved}
        />
      ))}
      {shortfall && (
        <div
          style={{
            display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8,
            padding: '8px 10px', borderRadius: 'var(--radius)',
            background: 'var(--amber-bg)', color: 'var(--text-secondary)',
          }}
        >
          <div style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 1 }}>
            <TriangleAlert size={14} />
          </div>
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}>
            The stakes do not sum to 100%: {moneyOrBlank(distribution.allocated)} of{' '}
            {moneyOrBlank(distribution.total)} is accounted for.
          </p>
        </div>
      )}
    </div>
  )
}

// El cuerpo de la cascada: siempre la de UN mes, venga del overview de la
// ventana (cuando la ventana es ese mes) o de la llamada aparte que la pide.
function Waterfall({ allocation }) {
  const { policy, policy_resolved: resolved, negative_excess: negative } = allocation
  // Sólo hay cascada que pintar cuando se resolvió una política Y hubo
  // excedente. Los otros dos estados llevan las filas en blanco o en cero, que
  // es lo que el backend manda en cada uno.
  const blank = resolved ? moneyOrBlank(0) : '—'

  return (
    <>
      {/* La política que gobierna este mes, con sus porcentajes tal cual los
          devuelve el endpoint. `effective_from` a la vista porque es lo que
          hace verificable que un mes viejo se calculó con la tasa de entonces. */}
      {policy && (
        <div
          style={{
            display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
            marginTop: 12, fontSize: 11.5, color: 'var(--text-muted)',
          }}
        >
          <span>Policy in force from {policy.effective_from}:</span>
          <span className="badge badge-gray">tax reserve {pctOrBlank(policy.tax_reserve_pct)}</span>
          <span className="badge badge-gray">emergency {pctOrBlank(policy.emergency_pct)}</span>
          <span className="badge badge-gray">growth {pctOrBlank(policy.growth_pct)}</span>
          <span className="badge badge-gray">debt {pctOrBlank(policy.debt_accel_pct)}</span>
          <span className="badge badge-gray">distribution {pctOrBlank(policy.distribution_pct)}</span>
        </div>
      )}

      {/* El estado sin política se anuncia ARRIBA y con sus propias palabras.
          Que las filas de abajo lleven '—' no basta por sí solo: un guion es
          ambiguo, y esto tiene que decir explícitamente que no se pudo
          calcular, no que el resultado fuera cero. */}
      {!resolved && (
        <div
          style={{
            marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius)',
            background: 'var(--amber-bg)', border: '1px solid var(--amber)',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            No allocation policy in force — nothing could be worked out
          </div>
          {/* El título dice lo que hay que saber; el `detail` del endpoint,
              literal, dice por qué un null aquí no es un cero. Plegado, porque
              son tres líneas sobre una tarjeta que ya lleva su titular. */}
          <DetailNote style={{ marginTop: 6 }} summary="Why the rows are blank, not zero" detail={allocation.detail} />
        </div>
      )}

      {/* El déficit es hoy el caso normal, así que se explica en tono neutro y
          sin señales de alarma: la cascada corre sobre excedente y un mes en
          déficit legítimamente no produce ninguno. Los ceros de abajo son
          reales — se calcularon. El único color aquí es el del propio
          `cash_excess`, que es negativo y lo dice. */}
      {resolved && negative && (
        <div
          style={{
            marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius)',
            background: 'var(--bg-tint)', border: '1px solid var(--border)',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            Nothing to allocate this month
          </div>
          <DetailNote style={{ marginTop: 6 }} summary="Why the rows are a real zero" detail={allocation.detail} />
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <WaterfallRow
          strong
          label="Cash excess"
          hint="Collections less everything that left the account this month."
          value={moneyOrBlank(allocation.cash_excess)}
          // Verde o rojo según el signo, y sin color cuando no hay cifra. Es la
          // única línea de la cascada que puede ser negativa, y el mes en
          // déficit es hoy el caso normal: el rojo dice qué pasó, no que algo
          // se haya roto.
          tone={signTone(allocation.cash_excess)}
        />
        <WaterfallRow
          label="Tax reserve"
          pct={policy ? pctOrBlank(policy.tax_reserve_pct) : null}
          hint="Held back before anything is divided."
          value={allocation.tax_reserve === null || allocation.tax_reserve === undefined ? blank : moneyOrBlank(allocation.tax_reserve)}
          muted={!resolved}
          // Ámbar, el mismo token que `reserve_estimated` en TaxLines y por el
          // mismo motivo: es dinero apartado, no dinero que se haya movido.
          tone="var(--amber)"
        />
        <WaterfallRow
          strong
          label="Post-tax to divide"
          value={allocation.post_tax === null || allocation.post_tax === undefined ? blank : moneyOrBlank(allocation.post_tax)}
          muted={!resolved}
        />
        <WaterfallRow
          indent
          label="Emergency fund"
          pct={policy ? pctOrBlank(policy.emergency_pct) : null}
          value={allocation.emergency === null || allocation.emergency === undefined ? blank : moneyOrBlank(allocation.emergency)}
          muted={!resolved}
        />
        <WaterfallRow
          indent
          label="Growth"
          pct={policy ? pctOrBlank(policy.growth_pct) : null}
          value={allocation.growth === null || allocation.growth === undefined ? blank : moneyOrBlank(allocation.growth)}
          muted={!resolved}
        />
        <WaterfallRow
          indent
          label="Accelerated debt repayment"
          pct={policy ? pctOrBlank(policy.debt_accel_pct) : null}
          value={allocation.debt_accel === null || allocation.debt_accel === undefined ? blank : moneyOrBlank(allocation.debt_accel)}
          muted={!resolved}
        />
        <WaterfallRow
          indent
          label="Distribution to partners"
          pct={policy ? pctOrBlank(policy.distribution_pct) : null}
          value={
            allocation.distribution?.total === null || allocation.distribution?.total === undefined
              ? blank
              : moneyOrBlank(allocation.distribution.total)
          }
          muted={!resolved}
        />
        <OwnerTable distribution={allocation.distribution} blank={blank} resolved={resolved} />
      </div>
    </>
  )
}

export default function AllocationWaterfall({
  allocation,
  month,
  rangeText,
  loading,
  focusAllocation,
  focusError,
}) {
  if (loading) {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Surplus allocation</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 10 }}>Loading…</p>
      </div>
    )
  }

  if (!allocation) return null

  // La bandera, y nunca la presencia de una clave. Ver la cabecera del fichero.
  // `=== false` y no `!allocation.applies_to_range`: una respuesta de un backend
  // anterior a este campo no trae la bandera, y ésa es la forma de un mes —
  // tratarla como la del rango escondería la cascada que sí tiene.
  const overRange = allocation.applies_to_range === false

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: 1 }}>
          <Split size={17} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Surplus allocation — {monthLabel(month)}</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            How cash left over is divided — a month that spent more than it collected produces none.
          </p>
        </div>
      </div>

      {/* Sobre un rango largo, lo primero y sin plegar: el `detail` del backend,
          literal. Explica por qué no hay cascada de la ventana mejor de lo que
          puede hacerlo esta pantalla —la política está versionada por fecha y
          un rango puede cruzar un cambio de ella— y reescribirlo aquí sería una
          segunda versión de esa razón que se desincroniza de la primera. */}
      {overRange && (
        <div
          style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius)',
            background: 'var(--amber-bg)', border: '1px solid var(--amber)',
          }}
        >
          <div style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 1 }}>
            <CalendarRange size={15} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              Showing {monthLabel(month)} alone — one month of {rangeText}
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Pick another with <strong>Month in focus</strong> above.
            </p>
            {/* El `detail` del backend, literal: explica por qué no hay cascada
                de la ventana —la política está versionada por fecha y un rango
                puede cruzar un cambio de ella— mejor de lo que puede hacerlo
                esta pantalla. El titular ya dice QUÉ se está viendo, así que el
                porqué puede ir plegado sin que se pierda nada. */}
            <DetailNote style={{ marginTop: 6 }} summary="Why the range has no waterfall of its own" detail={allocation.detail} />
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────
          Sobre un rango los tres estados son EXHAUSTIVOS, y ese es el punto

          El bloque de arriba acaba de prometer "las cifras de abajo son las de
          septiembre". Debajo tiene que haber siempre una de tres cosas: la
          cascada, el motivo por el que no está, o que se está trayendo. Nunca
          nada.

          El hueco silencioso existía y no era teórico: entre que llega la
          respuesta de la ventana y que arranca la segunda llamada hay un
          render, y ahí la tarjeta anunciaba unas cifras que no estaban sin
          decir por qué. Es peor que un cero — un cero al menos se puede
          cuestionar — y es justo el fallo que el resto de esta pantalla evita
          en todas partes.

          El orden importa: el error va PRIMERO. Con la carga delante, una
          segunda llamada que falla se queda en "Loading…" para siempre, que es
          la misma mentira con otra cara.
          ───────────────────────────────────────────────────────────────── */}
      {overRange ? (
        focusError ? (
          // Sin cascada, y nunca la del mes anterior: una tabla de cifras bajo
          // un encabezado que nombra otro mes es peor que no tener tabla. El
          // motivo del backend cuando lo trae; si no, el de `errorDetail`.
          <div
            style={{
              display: 'flex', gap: 8, alignItems: 'flex-start',
              marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius)',
              background: 'var(--red-bg)', border: '1px solid var(--red)',
            }}
          >
            <div style={{ color: 'var(--red)', flexShrink: 0, marginTop: 1 }}>
              <TriangleAlert size={15} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                {monthLabel(month)}'s allocation could not be loaded
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                {focusError}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                No figures are shown rather than the ones from another month. The rest of this
                screen is the window's and is unaffected.
              </p>
            </div>
          </div>
        ) : focusAllocation ? (
          <Waterfall allocation={focusAllocation} />
        ) : (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>
            Loading {monthLabel(month)}…
          </p>
        )
      ) : (
        <Waterfall allocation={allocation} />
      )}
    </div>
  )
}
